import round from 'lodash/round'
import { DateTime } from 'luxon'
import { distanceBetweenCoordinates } from '~/lib/geoUtils'
import {
  FilterTypes,
  RestaurantAreas,
  RestaurantLiteWithDistanceList,
} from '~/lib/interfaces/restaurant'
import { Coords } from '~/lib/models/geolocation'
import { StatusRestaurantState } from '~/lib/services/store/restaurant/restaurant.dto'

export const useRestaurantsMOP = () => {
  const countryCode = useCountry()
  const authStore = useAuthStore()
  const appStore = useAppStore()
  const { restaurantServiceRef } = useStoreService()

  const restaurantService = restaurantServiceRef()
  const KEY = `${countryCode}-restaurants-mop`

  const list = shallowRef<RestaurantLiteWithDistanceList>([])

  const isSSR = computed(() => import.meta.server)

  const getSessionItem = <T>(key: string): T => {
    if (isSSR.value) return [] as T
    const item = sessionStorage.getItem(key)
    if (!item) return [] as T
    return JSON.parse(item)
  }

  const setSessionItem = <T>(key: string, data: T) => {
    if (isSSR.value) return
    sessionStorage.setItem(key, JSON.stringify(data))
    list.value = data as RestaurantLiteWithDistanceList
  }

  const fetchListRestaurants = async () => {
    let restaurantsList: RestaurantLiteWithDistanceList = []
    if (isSSR.value) return
    if (appStore.isEcommerceDisabled) {
      setSessionItem<RestaurantLiteWithDistanceList>(KEY, restaurantsList)
      return
    }

    if (list.value?.length) return

    try {
      restaurantsList = await restaurantService.getStoreListRestaurant({
        area: RestaurantAreas.MOP,
        countryCode,
      })

      restaurantsList = restaurantsList.map(res => ({
        ...res,
        ecommerce: true,
        isFavorite: isFavoriteRestaurant({
          countryCode,
          favoriteRestaurantsMetadata: authStore?.userMetadata?.favoriteRestaurants,
          restaurantCode: res.code,
        }),
      }))
      setSessionItem<RestaurantLiteWithDistanceList>(KEY, restaurantsList)
    } catch (error) {
      //
      setSessionItem<RestaurantLiteWithDistanceList>(KEY, restaurantsList)
    }
  }

  const updateListByCoords = (coords: Coords) => {
    let restaurantsList = getSessionItem<RestaurantLiteWithDistanceList>(KEY)
    if (!restaurantsList?.length) return
    restaurantsList = restaurantsList.map(res => {
      return {
        ...res,
        distance: round(
          distanceBetweenCoordinates(
            coords.lat,
            coords.lng,
            res.coordinates.latitude,
            res.coordinates.longitude,
            'K'
          ),
          2
        ),
      }
    })
    setSessionItem<RestaurantLiteWithDistanceList>(KEY, restaurantsList)
  }

  const filterRestaurants = (filters: string[]) => {
    const restaurantList = getSessionItem<RestaurantLiteWithDistanceList>(KEY)
    if (!list.value) return
    if (!filters.length) list.value = restaurantList

    list.value = restaurantList.filter(restaurant => {
      let isValid = true
      filters.forEach(filter => {
        if (filter === FilterTypes.MCCAFE) {
          if (!restaurant.services) return
          isValid = isValid && restaurant.services[filter] === true
        }
        if (filter === FilterTypes.OPEN_NOW) {
          isValid = isValid && restaurant.statusRestaurant === StatusRestaurantState.OPENED
        }

        if (filter === FilterTypes.DRIVE_THRU) {
          const state = getRestaurantOpenState({
            datetime: DateTime.utc(),
            active: restaurant.active,
            timezone: restaurant.timezone,
            code: restaurant.code,
            timePeriods: restaurant.timeSlotsService,
            pickupTime: appStore.ecommerceConfig?.pickupTime || 0,
          })
          isValid = isValid && (state.aut === 'OPEN' || state.aut === 'NEAR_TO_CLOSE')
        }
      })
      return isValid
    })
  }

  onMounted(() => {
    list.value = getSessionItem<RestaurantLiteWithDistanceList>(KEY)
  })

  return {
    list,
    fetchListRestaurants,
    updateListByCoords,
    filterRestaurants,
  }
}
